import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import ProgressMessage from 'components/Utils/ProgressMessage';
import DefaultButton from 'components/Utils/NewDefaultButton';
import './style.scss';
import LargeResourcePreview from 'components/Utils/LargeResourcePreview/index';
import DocumentDetails from 'components/HealthRecords/Documents/DocumentDetails';
import DocumentListByCategory from 'components/HealthRecords/Documents/DocumentListByCategory';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import PrescriptionDetails from 'components/HealthRecords/Prescriptions/PrescriptionDetails';
import useDocumentSelection from 'hooks/useDocumentSelection';
import SelectableDocumentBadge from 'components/HealthRecords/Documents/SelectableDocumentBadge';

const AddDocumentAttachmentModal = (props) => {
  const { patient, useIds = false } = props;
  const patientId = patient.id;
  const dispatch = useAppDispatch();
  const patientHr = useAppSelector((state) => state.healthRecords.byId[patientId]);
  const patientPrescriptions = patientHr?.prescriptions;
  const patientDocuments = patientHr?.documents;

  const key = useIds ? 'id' : 'filename';

  const { selected, toggleDocument, isDocumentSelected, getDocumentIndex } = useDocumentSelection({
    initialSelected: [...(props.selected || [])],
    mode: 'key',
    keyName: key,
    getKeyFn: (document) => document[key] || document.attachments?.[0],
  });

  let [document, setDocument] = useState(undefined);
  let [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    dispatch(api.HealthRecords.getCPP({ patientId }));
    setLoading(false);
  }, [patientId, dispatch]);

  const documents = useMemo(() => {
    return _.reverse(_.sortBy(patientDocuments, ['date']));
  }, [patientDocuments]);

  const prescriptions = useMemo(() => {
    return _.reverse(_.sortBy(patientPrescriptions, ['date']));
  }, [patientPrescriptions]);

  return (
    <>
      <div className="main-content">
        <div className="attachment-list scroll-list full-height underside-right-panel">
          <ProgressMessage show={loading} />
          <DocumentListByCategory
            documents={documents}
            prescriptions={prescriptions}
            onDocumentClicked={setDocument}
            loading={loading}
            isItemSelected={(value) => value.id === document?.id}
            isItemChecked={isDocumentSelected}
            renderBadge={
              props.hideSend
                ? undefined
                : (document) => (
                    <SelectableDocumentBadge
                      document={document}
                      isChecked={isDocumentSelected(document)}
                      onToggle={toggleDocument}
                      index={getDocumentIndex(document)}
                      showOrder={true}
                      patient={patient}
                    />
                  )
            }
          />
        </div>
        {document !== undefined && (
          <>
            <div className="details-wrapper full-height">
              {!!document?.filename && <DocumentDetails document={document} disableEdit={true} />}
              {!document?.filename && !!document?.attachments && (
                <PrescriptionDetails prescription={document} />
              )}
            </div>
            {!!document?.filename && (
              <LargeResourcePreview
                key={(document || {}).id}
                autoLoad={true}
                getFile={(dispatchOptions) =>
                  dispatch(
                    api.Documents.getDocumentFile({ documentId: document?.id }),
                    dispatchOptions,
                  )
                }
                getFileInfo={() =>
                  dispatch(
                    api.Documents.getDocumentFileInfo({
                      documentId: document?.id,
                    }),
                  )
                }
              />
            )}
          </>
        )}
      </div>
      <div className="bottom-content">
        <DefaultButton
          name={`Save ${selected.length} attachments`}
          onClick={() => props.onSubmit(selected)}
          style={{
            width: '245px',
            marginTop: '5px',
            marginLeft: 'auto',
            marginRight: 'auto',
            display: 'block',
          }}
        />
      </div>
    </>
  );
};

AddDocumentAttachmentModal.propTypes = {
  patient: PropTypes.object,
  selected: PropTypes.array,
  onSubmit: PropTypes.func.isRequired,
};

export default AddDocumentAttachmentModal;
